import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import cookie from "react-cookies";
import { loadReCaptcha, ReCaptcha } from "react-recaptcha-v3";
import { postCandidateOtp, validateCandidateOtp } from "../../services/academyApi";
import { QtCheck, QtIcon } from "../../components/icons/QtIcon";
import { CAREERS_MEDIA, LOGIN_THUMBS } from "../careersMedia";
import CareersFooter from "../../components/CareersFooter";
import "./login.scss";

const COOKIE_OPTS = { path: "/", maxAge: 86400, secure: true };

const ACADEMY_BENEFITS = [
  "Free logistics training from industry experts",
  "Build a profile employers actually see",
  "Earn certificates as you complete modules",
  "Learn on your phone, at your own pace",
];

const CREATOR_BENEFITS = [
  "R300 per approved script",
  "Phone-first vertical clips from a clear brief",
  "Upload Hook A, B, C and a shared body",
  "Paid after QikTruck approves your work",
];

function saveCandidateSession(data) {
  const key = data.candidateKey || data.token;
  cookie.save("candidateKey", key, COOKIE_OPTS);
  cookie.save("candidate-qt", key, {
    ...COOKIE_OPTS,
    domain: "qiktruck.co.za",
  });
  cookie.save("candidate-qt-c", key, {
    ...COOKIE_OPTS,
    domain: "courses.qiktruck.co.za",
  });
  if (data.firstname) {
    cookie.save("candidateName", data.firstname, COOKIE_OPTS);
  }
  if (data.candidateid) {
    cookie.save("candidateId", data.candidateid, COOKIE_OPTS);
  }
}

function isValidEmail(email) {
  return email.length >= 5 && email.includes("@") && email.includes(".");
}

function isCreatorNext(nextPath) {
  const path = String(nextPath || "");
  return path.includes("/me/creator") || path === "/creator" || path.startsWith("/creator?");
}

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const nextUrl = searchParams.get("next") || "/academy";
  const creatorFlow = isCreatorNext(nextUrl);
  const prefillEmail = String(searchParams.get("email") || "")
    .trim()
    .toLowerCase();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(
    isValidEmail(prefillEmail) ? prefillEmail : ""
  );
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const recaptchaRef = useRef(null);
  const recaptchaToken = useRef("");
  const pendingAction = useRef(null);
  const benefits = creatorFlow ? CREATOR_BENEFITS : ACADEMY_BENEFITS;

  useEffect(() => {
    if (cookie.load("candidateKey")) {
      window.location.href = nextUrl;
    }
  }, [nextUrl]);

  useEffect(() => {
    if (!isValidEmail(prefillEmail)) return;
    setEmail((current) => (current ? current : prefillEmail));
  }, [prefillEmail]);

  useEffect(() => {
    try {
      loadReCaptcha(process.env.REACT_APP_RECAPTCHA_SITEKEY);
    } catch (e) {
      console.error("loadReCaptcha failed", e);
    }
  }, []);

  const refreshRecaptcha = useCallback(() => {
    try {
      recaptchaRef.current?.execute();
    } catch (e) {
      console.error("recaptcha execute failed", e);
    }
  }, []);

  const verifyCallback = useCallback((token) => {
    recaptchaToken.current = token;
    const action = pendingAction.current;
    pendingAction.current = null;
    if (action) action(token);
  }, []);

  const requestOtp = useCallback(
    (token) => {
      setLoading(true);
      setError("");
      postCandidateOtp(email, token)
        .then((res) => {
          if (res.ok) {
            setStep(2);
            setOtp("");
          } else {
            setError("Could not send the code. Please try again.");
            refreshRecaptcha();
          }
        })
        .catch(() => {
          setError("Could not send the code. Please try again or contact support.");
          refreshRecaptcha();
        })
        .finally(() => setLoading(false));
    },
    [email, refreshRecaptcha]
  );

  const handleContinue = (e) => {
    e.preventDefault();
    if (!isValidEmail(email) || loading) return;
    pendingAction.current = requestOtp;
    refreshRecaptcha();
  };

  const handleResend = () => {
    if (loading) return;
    pendingAction.current = requestOtp;
    refreshRecaptcha();
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!otp.trim() || loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await validateCandidateOtp(email, otp.trim());
      if (res.ok) {
        const data = await res.json();
        saveCandidateSession(data);
        window.location.href = nextUrl;
        return;
      }
      setError("Invalid or expired code. Please try again.");
    } catch {
      setError("Sign in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (cookie.load("candidateKey")) {
    return (
      <div className="login-page">
        <div className="login-page__redirecting">Redirecting...</div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-page__bg" aria-hidden="true" />

      <header className="login-nav">
        <Link to="/" className="login-nav__brand">
          QikTruck <span>Careers</span>
        </Link>
        <Link to={creatorFlow ? "/creator" : "/academy"} className="login-nav__link">
          {creatorFlow ? "Creator Program" : "Browse programs"}
        </Link>
      </header>

      <main className="login-main">
        <section className="login-visual">
          <div className="login-visual__hero">
            <img
              src={CAREERS_MEDIA.heroMain}
              alt={
                creatorFlow
                  ? "Film QikTruck Creator scripts on your phone and get paid"
                  : "Build your logistics career with QikTruck training programs in South Africa"
              }
              loading="eager"
              fetchPriority="high"
              width={1400}
              height={933}
              style={{ objectPosition: "center 25%" }}
            />
            <div className="login-visual__badge">
              <span className="login-visual__badge-icon" aria-hidden="true">
                <QtIcon name="book-open" size={18} />
              </span>
              <span>{creatorFlow ? "Creator invite" : "Free program access"}</span>
            </div>
          </div>

          <div className="login-visual__copy">
            <p className="login-hero__eyebrow">
              {creatorFlow ? "QikTruck Creator Program" : "QikTruck Programs"}
            </p>
            <h1 className="login-hero__title">
              {creatorFlow ? "Sign in to start filming" : "Build your logistics career"}
            </h1>
            <p className="login-hero__subtitle">
              {creatorFlow
                ? "Use the email from your invite. We will send a one-time code, then open Creator Scripts."
                : "Sign in to access training, track your progress, and stand out to employers across South Africa."}
            </p>
            <ul className="login-hero__benefits">
              {benefits.map((text) => (
                <li key={text}>
                  <span className="login-hero__check" aria-hidden="true">
                    <QtCheck size={12} />
                  </span>
                  {text}
                </li>
              ))}
            </ul>
          </div>

          <div className="login-visual__strip">
            {LOGIN_THUMBS.map((thumb) => (
              <figure key={thumb.src} className="login-visual__thumb">
                <img
                  src={thumb.src}
                  alt={thumb.alt}
                  loading="lazy"
                  width={480}
                  height={360}
                />
                <figcaption>{thumb.label}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="login-card">
          {step === 1 ? (
            <form onSubmit={handleContinue} noValidate>
              <h2 className="login-card__heading">
                {creatorFlow ? "Continue to Creator Scripts" : "Sign in"}
              </h2>
              <p className="login-card__hint">
                {creatorFlow && isValidEmail(email)
                  ? "Confirm your invite email and we will send a one-time code."
                  : "Enter your email and we will send you a one-time code."}
              </p>

              {error && (
                <div className="login-alert" role="alert">
                  {error}
                </div>
              )}

              <label className="login-field">
                <span className="login-field__label">Email address</span>
                <input
                  type="email"
                  className="login-field__input"
                  placeholder="you@email.com"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  autoComplete="email"
                  autoFocus={!isValidEmail(email)}
                  required
                />
              </label>

              <ReCaptcha
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITEKEY}
                action="candidate_login_email"
                verifyCallback={verifyCallback}
              />

              <button
                type="submit"
                className="login-btn-primary"
                disabled={!isValidEmail(email) || loading}
              >
                {loading ? "Sending..." : "Continue"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSignIn} noValidate>
              <h2 className="login-card__heading">Check your email</h2>
              <p className="login-card__hint">
                We sent a code to <strong>{email}</strong>
              </p>

              {error && (
                <div className="login-alert" role="alert">
                  {error}
                </div>
              )}

              <label className="login-field">
                <span className="login-field__label">One-time code</span>
                <input
                  type="text"
                  className="login-field__input login-field__input--otp"
                  placeholder="Enter code"
                  value={otp}
                  onChange={(ev) => setOtp(ev.target.value.toUpperCase())}
                  autoComplete="one-time-code"
                  autoFocus
                  required
                />
              </label>

              <button
                type="submit"
                className="login-btn-primary"
                disabled={!otp.trim() || loading}
              >
                {loading ? "Signing in..." : "Sign in"}
              </button>

              <div className="login-card__actions">
                <button
                  type="button"
                  className="login-btn-text"
                  onClick={handleResend}
                  disabled={loading}
                >
                  Resend code
                </button>
                <button
                  type="button"
                  className="login-btn-text"
                  onClick={() => {
                    setStep(1);
                    setOtp("");
                    setError("");
                  }}
                  disabled={loading}
                >
                  Change email
                </button>
              </div>
            </form>
          )}
        </section>
      </main>

      <CareersFooter />
    </div>
  );
}
